import { useState, type FormEvent } from 'react'
import { api, can, refusalReason, saveFile, shortDate, type ImportRestoreResult, type ImportUndoResult } from '../lib/api'
import { errorWords, needsReference, plural } from '../lib/importWords'
import { ErrorReferenceOf, Icon } from './ui'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/import.css'

// What an import leaves behind that a person can still act on (release 3.2):
// the count report, Undo inside its window, and Restore after an undo. Used on
// the wizard's last step and on Earlier imports, so the two say the same thing.
//
// Undo never deletes. Vendors nobody has worked on since the import are taken
// off the register with their details kept; fields the import changed are put
// back. A reason is always asked for, because the record of an undo is read by
// somebody who was not there.

// A failure said in place, with what to do next. The code and reference are
// shown only when the trouble is the server's, which is when support needs
// them.
export function ImportError({ error }: { error: unknown }) {
  if (error == null) return null
  return (
    <div className="note stop" role="alert" style={{ marginTop: 12 }}>
      {errorWords(error)}
      {needsReference(error) && <ErrorReferenceOf error={error} />}
    </div>
  )
}

export function CountReportButton({ importId, label = 'Download report' }: { importId: string; label?: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setError(null)
          try {
            saveFile(await api.importReportCsv(importId))
          } catch (err) {
            setError(err)
          } finally {
            setBusy(false)
          }
        }}
      >
        <Icon name="file" size={15} /> {busy ? 'Preparing the report' : label}
      </button>
      {error != null && <ImportError error={error} />}
    </>
  )
}

// The reason box and the button that undoes. The caller shows it when the
// person asks to undo, and hides it on Cancel.
export function UndoForm({
  importId,
  undoUntil,
  onDone,
  onCancel,
}: {
  importId: string
  undoUntil?: string
  onDone: (r: ImportUndoResult) => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const allowed = can('vendor.create')
  const fieldId = 'undo-reason-' + importId

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!reason.trim() || !allowed) return
    setBusy(true)
    setError(null)
    try {
      onDone(await api.undoImport(importId, reason.trim()))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="imw-form"
      onSubmit={submit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
      aria-label="Undo this import"
    >
      <p style={{ margin: 0 }}>
        Vendors nobody has worked on since the import are taken off the register. Their details are kept
        {undoUntil ? ', and you can restore them until ' + shortDate(undoUntil) : ''}. Details this import changed on
        vendors you already had are put back.
      </p>
      <label htmlFor={fieldId}>Why are you undoing it? *</label>
      <textarea
        id={fieldId}
        required
        aria-required="true"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="For example: this was a test file"
      />
      <div className="p1-actions">
        <button
          type="submit"
          className="primary"
          disabled={busy || !reason.trim() || !allowed}
          title={allowed ? undefined : refusalReason('vendor.create')}
        >
          {busy ? 'Undoing' : 'Undo the import'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        {!allowed && <ReasonTip label="Why you cannot undo it" text={refusalReason('vendor.create')} />}
      </div>
      {!reason.trim() && <span className="imw-small">Write a short reason to undo it.</span>}
      {error != null && <ImportError error={error} />}
    </form>
  )
}

// What an undo did: how many vendors came off, what was kept and why.
export function UndoSaid({ result, undoUntil }: { result: ImportUndoResult; undoUntil?: string }) {
  const kept = result.kept ?? []
  return (
    <div className="note" role="status" style={{ marginTop: 12 }}>
      <strong>
        {plural(result.retired, 'vendor')} taken off the register
        {result.undoneAt ? ' on ' + shortDate(result.undoneAt) : ''}
      </strong>
      <ul className="imw-sum" style={{ margin: '4px 0 0' }}>
        <li>Their details are kept{undoUntil ? '. You can restore them until ' + shortDate(undoUntil) : ''}.</li>
        {(result.fieldsRestored ?? 0) > 0 && (
          <li>{plural(result.fieldsRestored ?? 0, 'detail')} on vendors you already had put back as they were.</li>
        )}
        {(result.fieldsKept ?? 0) > 0 && (
          <li>
            {plural(result.fieldsKept ?? 0, 'detail')} left as {result.fieldsKept === 1 ? 'it is' : 'they are'}, because
            someone changed {result.fieldsKept === 1 ? 'it' : 'them'} after the import.
          </li>
        )}
        {(result.documentsDetached ?? 0) > 0 && (
          <li>{plural(result.documentsDetached ?? 0, 'document')} taken off their vendors.</li>
        )}
      </ul>
      {kept.length > 0 && (
        <>
          <p style={{ margin: '8px 0 2px' }}>
            {plural(kept.length, 'vendor')} kept on the register, because someone has worked on {kept.length === 1 ? 'it' : 'them'}:
          </p>
          <ul className="p1-inline-list">
            {kept.map((k, i) => (
              <li key={k.name + i}>
                {k.name}: {k.reason}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

// Restore after an undo, inside the same window.
export function RestoreForm({
  importId,
  onDone,
  onCancel,
}: {
  importId: string
  onDone: (r: ImportRestoreResult) => void
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const allowed = can('vendor.create')
  return (
    <div
      className="imw-form"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <p style={{ margin: 0 }}>The vendors this undo took off the register are put back, with their details.</p>
      <div className="p1-actions">
        <button
          type="button"
          className="primary"
          disabled={busy || !allowed}
          title={allowed ? undefined : refusalReason('vendor.create')}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onDone(await api.restoreImport(importId))
            } catch (err) {
              setError(err)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Restoring' : 'Restore this import'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        {!allowed && <ReasonTip label="Why you cannot restore it" text={refusalReason('vendor.create')} />}
      </div>
      {error != null && <ImportError error={error} />}
    </div>
  )
}

export function RestoreSaid({ result }: { result: ImportRestoreResult }) {
  const not = result.notRestored ?? []
  return (
    <div className="note" role="status" style={{ marginTop: 12 }}>
      <strong>{plural(result.restored, 'vendor')} back on the register</strong>
      {not.length > 0 && (
        <>
          <p style={{ margin: '4px 0 2px' }}>{plural(not.length, 'vendor')} could not be put back:</p>
          <ul className="p1-inline-list">
            {not.map((k, i) => (
              <li key={k.name + i}>
                {k.name}: {k.reason}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
